import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import './Login.css';
import { GoogleLogin } from '@react-oauth/google';
import logoImg from '../../static/logo.svg';

declare global {
    interface Window {
        google?: {
            accounts: {
                id: {
                    initialize: (config: any) => void;
                    renderButton: (container: HTMLElement, options: any) => void;
                    prompt: () => void;
                }
            }
        }
    }
}

interface LoginResponse {
    token: string;
    refresh: string;
    user: {
        id: number;
        email: string;
        balance: string;
    };
}

interface RegisterResponse {
    message: string;
    token: string;
    refresh: string;
    user: {
        id: number;
        email: string;
        balance: string;
    };
}

interface FormData {
    email: string;
    password: string;
    confirmPassword?: string;
    firstName?: string;
    lastName?: string;
    rememberMe: boolean;
}

interface GoogleAuthResponse {
    token: string;
    refresh: string;
    user: {
        id: number;
        email: string;
        balance: string;
    };
}

export const Login: React.FC = () => {
    const [isSignUp] = useState(false);
    const [formData, setFormData] = useState<FormData>({
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        rememberMe: false
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showPassword, setShowPassword] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        
        const data = {
            email: formData.email,
            password: formData.password,
            ...(isSignUp && { 
                confirm_password: formData.confirmPassword,
                first_name: formData.firstName,
                last_name: formData.lastName
            })
        };
        
        console.log('Submitting form:', {
            mode: isSignUp ? 'signup' : 'signin',
            email: data.email,
            hasPassword: !!data.password
        });
        
        try {
            if (isSignUp) {
                if (formData.password !== formData.confirmPassword) {
                    throw new Error('Passwords do not match');
                }
                console.log('Attempting registration...');
                const registerResponse = await api.post<RegisterResponse>('/auth/register/', {
                    email: formData.email,
                    password: formData.password,
                    confirm_password: formData.confirmPassword,
                    first_name: formData.firstName,
                    last_name: formData.lastName
                });
                
                // Store tokens after registration
                localStorage.setItem('token', registerResponse.data.token);
                localStorage.setItem('refresh', registerResponse.data.refresh);
                
                // Fetch user data immediately after registration
                const userResponse = await api.get('/auth/user/');
                if (userResponse.data) {
                    // Force page reload to update context
                    window.location.href = '/';
                }
                return;
            }
            
            console.log('Attempting login...');
            const response = await api.post<LoginResponse>('/auth/login/', {
                email: formData.email,
                password: formData.password,
                remember_me: formData.rememberMe
            });

            if (response?.data?.token) {
                localStorage.setItem('token', response.data.token);
                localStorage.setItem('refresh', response.data.refresh);
                
                // Fetch user data immediately after login
                const userResponse = await api.get('/auth/user/');
                if (userResponse.data) {
                    window.location.href = '/';
                }
            } else {
                throw new Error('Invalid response from server');
            }
        } catch (err: any) {
            console.error('Auth error:', {
                status: err.response?.status,
                data: err.response?.data,
                message: err.message
            });
            
            // Handle specific error messages from backend
            if (err.response?.data?.error) {
                setError(err.response.data.error);
            } else if (err.response?.data?.errors) {
                // Handle validation errors
                const errors = err.response.data.errors;
                const errorMessage = Object.values(errors).flat().join(', ');
                setError(errorMessage);
            } else {
                setError(err.message || (isSignUp ? 'Failed to sign up' : 'Failed to sign in'));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSuccess = async (credentialResponse: any) => {
        try {
            setLoading(true);
            setError(null);
            
            const token = credentialResponse.credential;
            
            // Send the token to your backend
            const response = await api.post<GoogleAuthResponse>('/auth/google/', {
                token: token
            });

            if (response?.data?.token) {
                console.log('Authentication successful');
                localStorage.setItem('token', response.data.token);
                localStorage.setItem('refresh', response.data.refresh);
                
                // Fetch user data
                const userResponse = await api.get('/auth/user/');
                
                if (userResponse.data) {
                    window.location.href = '/';
                }
            }
        } catch (err: any) {
            console.error('Google auth error:', {
                message: err.message,
                response: err.response?.data,
                status: err.response?.status
            });
            setError('Failed to sign in with Google');
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleError = () => {
        setError('Google sign in was unsuccessful');
    };

    return (
        <div className="login-page">
            <div className="login-container">
                <div className="login-card">
                    <div className="login-logo">
                        <img src={logoImg} alt="Logo" className="logo-img" />
                        <div className="logo-text">
                            <span className="logo-text-primary">Child</span>
                            <span className="logo-text-secondary">Story Time</span>
                        </div>
                    </div>
                    
                    <h2 className="login-title">Log In to childstory.ai</h2>
                    
                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label>Email address</label>
                            <div className="input-wrapper">
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData(prev => ({
                                        ...prev,
                                        email: e.target.value
                                    }))}
                                    placeholder="Enter your email address"
                                    required
                                />
                                <i className="email-icon"></i>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Password</label>
                            <div className="input-wrapper">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    value={formData.password}
                                    onChange={(e) => setFormData(prev => ({
                                        ...prev,
                                        password: e.target.value
                                    }))}
                                    placeholder="Enter your password"
                                    required
                                />
                                <button 
                                    type="button" 
                                    className="password-toggle"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? 
                                        <i className="eye-slash-icon"></i> : 
                                        <i className="eye-icon"></i>
                                    }
                                </button>
                            </div>
                        </div>

                        <div className="form-options">
                            <div className="remember-me">
                                <input
                                    type="checkbox"
                                    id="rememberMe"
                                    checked={formData.rememberMe}
                                    onChange={(e) => setFormData(prev => ({
                                        ...prev,
                                        rememberMe: e.target.checked
                                    }))}
                                />
                                <label htmlFor="rememberMe">Remember me</label>
                            </div>
                            <Link to="/forgot-password" className="forgot-password">Forgot Password?</Link>
                        </div>

                        <button 
                            type="submit" 
                            className="login-button"
                            disabled={loading}
                        >
                            {loading ? 'Processing...' : 'Log In'}
                        </button>
                    </form>

                    <div className="divider">
                        <span>Or</span>
                    </div>

                    <div className="social-login">
                        <GoogleLogin
                            onSuccess={handleGoogleSuccess}
                            onError={handleGoogleError}
                            useOneTap={false}
                            type="standard"
                            text="continue_with"
                            shape="rectangular"
                            logo_alignment="left"
                            theme="filled_blue"
                            size="large"
                        />
                    </div>

                    <div className="signup-prompt">
                        <p>Don't have an account? <Link to="/signup">Sign Up</Link></p>
                    </div>

                    {error && <div className="error-message">{error}</div>}
                </div>
            </div>
        </div>
    );
}; 